/**
 * TabletTireCheckin — Schermata check-in per il tecnico in officina.
 * Layout minimalista full-screen, touch-friendly, light theme.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Card, Button, Space, Typography, Alert, Divider, Tag, Spin, message, theme,
} from 'antd'
import { QrcodeOutlined, ToolOutlined, CarOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { useScanQr, useCreateOrdineLavoro } from '@/hooks/useTireService'
import type { QrScanResult } from '@/types/tireService'
import QrScanner from '@/components/QrScanner'

const { Title, Text } = Typography

export default function TabletTireCheckin() {
  const { token } = theme.useToken()
  const [scannerActive, setScannerActive] = useState(false)
  const [result, setResult] = useState<QrScanResult | null>(null)
  const [scanned, setScanned] = useState(false)
  const navigate = useNavigate()

  const scanQr = useScanQr()
  const createOrdine = useCreateOrdineLavoro()

  const handleScan = async (codice: string) => {
    if (scanned) return
    setScanned(true)
    setScannerActive(false)
    try {
      const res = await scanQr.mutateAsync(codice.trim())
      setResult(res)
    } catch {
      message.error('QR non trovato o già utilizzato')
      setScanned(false)
    }
  }

  const handleCreaOrdine = async () => {
    if (!result) return
    try {
      const ordine = await createOrdine.mutateAsync({ prenotazione_id: result.prenotazione.id })
      navigate(`/tablet/tire-workorder/${ordine.id}`)
    } catch {
      message.error('Errore nella creazione dell\'ordine di lavoro')
    }
  }

  const handleReset = () => {
    setResult(null)
    setScanned(false)
    setScannerActive(false)
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: token.colorBgLayout,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '32px 16px',
      }}
    >
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <QrcodeOutlined style={{ fontSize: 48, color: '#1677ff' }} />
        <Title level={2} style={{ marginTop: 8 }}>Check-in Pneumatici</Title>
        <Text type="secondary" style={{ fontSize: 16 }}>
          Premi il pulsante per scansionare il QR del cliente
        </Text>
      </div>

      {/* Pulsante scanner / stop */}
      {!result && (
        <Card style={{ width: '100%', maxWidth: 500, marginBottom: 24, textAlign: 'center' }}>
          {!scannerActive ? (
            <Button
              type="primary"
              size="large"
              icon={<QrcodeOutlined />}
              onClick={() => { setScannerActive(true); setScanned(false) }}
              style={{ height: 64, fontSize: 20, width: '100%' }}
            >
              Scansiona QR
            </Button>
          ) : (
            <Button
              danger
              size="large"
              icon={<CloseCircleOutlined />}
              onClick={() => setScannerActive(false)}
              style={{ height: 48, fontSize: 16, width: '100%', marginBottom: 16 }}
            >
              Annulla scansione
            </Button>
          )}

          {/* Camera viewer */}
          <QrScanner active={scannerActive} onScan={handleScan} />

          {scanQr.isPending && (
            <div style={{ marginTop: 24 }}>
              <Spin size="large" tip="Verifica in corso…" />
            </div>
          )}
        </Card>
      )}

      {/* Risultato scan */}
      {result && (
        <Card
          style={{ width: '100%', maxWidth: 500 }}
          styles={{ body: { padding: 24 } }}
        >
          {/* Cliente + Veicolo */}
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <CarOutlined style={{ fontSize: 36, color: '#1677ff' }} />
            <Title level={3} style={{ marginTop: 8, marginBottom: 4 }}>
              {result.customer.nome} {result.customer.cognome}
            </Title>
            <Tag color="blue" style={{ fontSize: 16, padding: '4px 12px' }}>
              {result.vehicle.targa}
            </Tag>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">{result.vehicle.marca} {result.vehicle.modello}</Text>
            </div>
          </div>

          <Divider />

          {/* Tipo servizio */}
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Text strong style={{ fontSize: 18 }}>
              {result.prenotazione.tipo_servizio.replace(/_/g, ' ')}
            </Text>
          </div>

          {/* Set in deposito */}
          {result.set_in_deposito ? (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 24, fontSize: 16 }}
              message={
                <span>
                  Set trovato: <strong>{result.set_in_deposito.misura} {result.set_in_deposito.stagione}</strong>
                  {result.set_in_deposito.posizione && (
                    <Tag color="blue" style={{ marginLeft: 8, fontSize: 15 }}>
                      Scaffale: {result.set_in_deposito.posizione}
                    </Tag>
                  )}
                </span>
              }
            />
          ) : (
            <Alert
              type="warning"
              showIcon
              message="Nessun set in deposito trovato per questo veicolo"
              style={{ marginBottom: 24 }}
            />
          )}

          {/* CTA */}
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Button
              type="primary"
              block
              size="large"
              icon={<ToolOutlined />}
              loading={createOrdine.isPending}
              onClick={handleCreaOrdine}
              style={{ height: 56, fontSize: 18 }}
            >
              Apri Ordine di Lavoro
            </Button>
            <Button
              block
              size="large"
              icon={<QrcodeOutlined />}
              onClick={handleReset}
              style={{ height: 56, fontSize: 18 }}
            >
              Nuova Scansione
            </Button>
          </Space>
        </Card>
      )}
    </div>
  )
}
